import { useState, useEffect } from 'react';
import { ShieldCheck, Eye, FileEdit, FilePlus, Loader2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../api/client';

const ACTION_CFG = {
  view:   { Icon: Eye,      color: 'bg-blue-50 text-blue-600',       label: 'Viewed' },
  create: { Icon: FilePlus, color: 'bg-emerald-50 text-emerald-600', label: 'Created' },
  update: { Icon: FileEdit, color: 'bg-amber-50 text-amber-600',     label: 'Updated' },
  delete: { Icon: FileEdit, color: 'bg-red-50 text-red-600',         label: 'Deleted' },
};

const RESOURCE_LABELS = {
  PatientProfile: 'Health Record',
  Symptom:        'Symptom Report',
  CareEvent:      'Care Event',
  RiskAssessment: 'Risk Assessment',
  Response:       'Medical Note',
  Action:         'Care Action',
};

const DAYS_OPTIONS = [
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 60 days', value: 60 },
  { label: 'Last 90 days', value: 90 },
];

const PAGE_SIZE = 15;

export default function PatientAccessLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);

  const fetchLogs = async (p = page) => {
    setLoading(true);
    try {
      const res = await api.get(`/patient-profile/me/access-log?days=${days}&page=${p}&limit=${PAGE_SIZE}`);
      if (res.data.success) {
        setLogs(res.data.data || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchLogs(1);
  }, [days]);

  const goTo = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    setPage(next);
    fetchLogs(next);
  };

  return (
    <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-headline text-base font-bold text-slate-900">Who Accessed My Records</h3>
            <p className="text-xs text-slate-400 font-semibold">{total} access event{total !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="text-xs font-bold text-slate-600 border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            {DAYS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={() => fetchLogs(page)}
            className="p-2 rounded-xl hover:bg-slate-50 text-slate-400 hover:text-primary transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* HIPAA notice */}
      <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl flex items-start gap-3">
        <ShieldCheck className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-700 font-medium leading-relaxed">
          Per HIPAA, you have the right to know who has accessed your health information.
          Names are partially masked to protect privacy while maintaining accountability.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-10">
          <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-3">
            <ShieldCheck className="w-6 h-6 text-slate-300" />
          </div>
          <p className="text-sm font-bold text-slate-600">No access events in this period</p>
          <p className="text-xs text-slate-400 mt-1">Your records have not been accessed recently.</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {logs.map((log, i) => {
              const actionCfg = ACTION_CFG[log.action] || ACTION_CFG.view;
              const ActionIcon = actionCfg.Icon;
              const resource = RESOURCE_LABELS[log.resourceType] || log.resourceType || 'Record';
              const roleLabel = log.accessorRole === 'DOCTOR' ? 'Doctor' : log.accessorRole === 'ADMIN' ? 'Admin' : 'System';
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3.5 bg-slate-50 rounded-xl border border-outline-variant/10 hover:bg-white transition-colors"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${actionCfg.color}`}>
                    <ActionIcon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800">
                      {log.accessorName || 'Unknown'}{' '}
                      <span className="font-normal text-slate-500">({roleLabel})</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {actionCfg.label} your {resource}
                    </p>
                  </div>
                  <p className="text-xs text-slate-400 font-semibold shrink-0">
                    {new Date(log.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{' '}
                    {new Date(log.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs text-slate-400 font-medium">
                Page {page} of {totalPages} · {total} total
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => goTo(page - 1)}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === '…' ? (
                      <span key={`ellipsis-${idx}`} className="px-1 text-xs text-slate-400">…</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => goTo(item)}
                        className={`w-7 h-7 rounded-lg text-xs font-bold transition-colors ${
                          item === page
                            ? 'bg-primary text-white'
                            : 'hover:bg-slate-100 text-slate-600'
                        }`}
                      >
                        {item}
                      </button>
                    )
                  )}
                <button
                  onClick={() => goTo(page + 1)}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
